import { Link } from "react-router-dom";
import { MapPin, Briefcase, CheckCircle, ArrowRight } from "lucide-react";
import { useScrollAnimation } from "@/hooks/useScrollAnimation";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { cn } from "@/lib/utils";
import dallasHeadshot from "@/assets/dallas-headshot-3.png";

const credentials = [
  { icon: MapPin, label: "Based in", value: "Michigan" },
  { icon: Briefcase, label: "Approach", value: "Hands-on" },
  { icon: CheckCircle, label: "Reporting", value: "Transparent" },
];

const MeetFounder = () => {
  const { ref, isInView } = useScrollAnimation({ threshold: 0.1 });

  return (
    <section className="py-20 lg:py-28 bg-muted/30 relative overflow-hidden">
      {/* Subtle background accent */}
      <div className="absolute top-1/2 left-1/4 w-96 h-96 bg-accent/5 rounded-full blur-3xl -translate-y-1/2" />
      <div className="absolute bottom-0 right-1/4 w-64 h-64 bg-primary/5 rounded-full blur-3xl" />

      <div className="container mx-auto px-4 relative z-10">
        <div
          ref={ref}
          className={cn(
            "grid lg:grid-cols-2 gap-12 lg:gap-16 items-center transition-all duration-700",
            isInView ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"
          )}
        >
          {/* Photo Section - Desktop only */}
          <div className="hidden lg:flex justify-center lg:justify-end">
            <div className="relative group">
              {/* Glow effect behind image */}
              <div className="absolute inset-0 bg-gradient-to-br from-accent/20 to-primary/20 rounded-full blur-2xl scale-110 opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
              
              {/* Main image container */}
              <div className="relative">
                <img
                  src={dallasHeadshot}
                  alt="Dallas Valentine - Founder & President of Valentine Digital"
                  className="w-64 h-64 md:w-80 md:h-80 lg:w-96 lg:h-96 rounded-full object-cover ring-4 ring-accent/30 shadow-2xl group-hover:ring-accent/50 group-hover:scale-[1.02] transition-all duration-500"
                />
                
                {/* Decorative ring */}
                <div className="absolute inset-0 rounded-full ring-2 ring-accent/10 ring-offset-4 ring-offset-muted/30 group-hover:ring-accent/20 transition-all duration-500" />
              </div>
            </div>
          </div>

          {/* Content Section - Right on desktop, top on mobile */}
          <div className="space-y-6">
            {/* Badge */}
            <Badge variant="premium" className="text-sm">
              Meet The Founder
            </Badge>

            {/* Name & Title */}
            <div>
              <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-foreground mb-2">
                Dallas Valentine
              </h2>
              <p className="text-lg text-accent font-medium">Founder & President</p>
            </div>

            {/* Photo - Mobile only, appears after title */}
            <div className="flex lg:hidden justify-center">
              <div className="relative">
                <img
                  src={dallasHeadshot}
                  alt="Dallas Valentine - Founder & President of Valentine Digital"
                  className="w-48 h-48 md:w-64 md:h-64 rounded-full object-cover ring-4 ring-accent/30 shadow-2xl"
                />
              </div>
            </div>

            {/* Quote */}
            <blockquote className="relative pl-6 py-4 border-l-4 border-accent/50 bg-gradient-to-r from-accent/5 to-transparent rounded-r-lg">
              <p className="text-lg md:text-xl text-muted-foreground italic leading-relaxed">
                "Every business deserves a partner who's as invested in their success as they are. That's why I built Valentine Digital—to bring enterprise-level expertise with personal, hands-on service."
              </p>
            </blockquote>

            {/* Story */}
            <p className="text-muted-foreground leading-relaxed">
              I founded Valentine Digital to bridge the gap between big-agency capabilities and
              personal service. Based in Michigan's Thumb region, we work directly with clients on
              practical marketing, websites, and business systems without layers of account managers.
            </p>

            {/* Credentials */}
            <div className="grid grid-cols-3 gap-3 pt-4">
              {credentials.map((credential, index) => (
                <Card
                  key={credential.label}
                  variant="glass"
                  className={cn(
                    "p-4 text-center transition-all duration-500",
                    isInView ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"
                  )}
                  style={{ transitionDelay: `${(index + 1) * 100}ms` }}
                >
                  <credential.icon className="w-5 h-5 mx-auto mb-2 text-accent" />
                  <p className="text-sm font-bold text-foreground">{credential.value}</p>
                  <p className="text-xs text-muted-foreground">{credential.label}</p>
                </Card>
              ))}
            </div>

            {/* CTA */}
            <div className="pt-4">
              <Button asChild variant="premium" size="lg">
                <Link to="/about/dallas" className="group">
                  Learn More About Dallas
                  <ArrowRight className="ml-2 h-4 w-4 group-hover:translate-x-1 transition-transform" />
                </Link>
              </Button>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default MeetFounder;
